import { useState } from "react";
import type { DividersSetByHand } from "@CommonTypes/backend/PspRules";
import type { PlanogramShelf } from "@CommonTypes/merchflow/pog/shelf";
import { Color, Flex, Radios, Text } from "src/elements";
import { useTranslation } from "src/i18n";
import type { TranslationKey } from "src/i18n/hooks/UseTranslation";

type OverrideChoice = "follow" | DividersSetByHand;

/** The same three choices on the shelf panel and the Fixtures page. */
export const dividerOverrideOptions = (
  t: (key: TranslationKey<"common">) => string,
): Array<{ label: string; value: OverrideChoice }> => [
  { label: t("shelfDividers.override.follow"), value: "follow" },
  { label: t("shelfDividers.override.always"), value: "always" },
  { label: t("shelfDividers.override.never"), value: "never" },
];

export type SetShelfDividersByHand = (
  shelf: PlanogramShelf,
  value: DividersSetByHand | null,
) => Promise<boolean>;

/** Follow the flow's divider rule on this store's shelf, or set it by hand. */
export const ShelfDividerOverride = ({
  shelf,
  onSave,
}: {
  shelf: PlanogramShelf;
  onSave: SetShelfDividersByHand;
}) => {
  const { t } = useTranslation("common");
  const [isSaving, setIsSaving] = useState(false);
  const [message, setMessage] = useState<string | null>(null);
  const value: OverrideChoice = shelf.dividersSetByHand ?? "follow";

  const save = async (choice: OverrideChoice) => {
    if (choice === value || isSaving) return;
    setIsSaving(true);
    setMessage(null);
    const isSaved = await onSave(shelf, choice === "follow" ? null : choice);
    setIsSaving(false);
    setMessage(
      isSaved
        ? t("shelfDividers.override.saved", { bay: shelf.bay.getNo(), shelf: shelf.getNo() })
        : t("shelfDividers.override.notSaved"),
    );
  };

  return (
    <Flex column gap="4px" padding="4px 0">
      <Text variant="caption1" color={Color.primary}>
        {t("shelfDividers.override.title")}
      </Text>
      <Radios
        options={dividerOverrideOptions(t)}
        value={value}
        setValue={(choice) => void save(choice)}
        ariaLabel={t("shelfDividers.override.title")}
        size="small"
        isDisabled={isSaving}
      />
      {message && (
        <Text variant="caption1" color={Color.textSecondary} aria-live="polite">
          {message}
        </Text>
      )}
    </Flex>
  );
};
